<template>
  <div class="container">
    <remove-warning
      v-if="hasRemoveEvent"
      :removeEvent="getRemoveEvent"
      label="Warning.eventRemove.label"
      content="Warning.eventRemove.content"
      @remove="removeEvent"
      @cancel="closeRemoveWarning" />

    <h4 class="text-center mb-4">
      {{ $t('Campaign.preview.message') }}
    </h4>

    <div class="row m-0 m-0">
      <p class="col pl-0 pr-0 m-0">
        <span class="float-left centering">
          <h4 class="campaign-title">
            {{ getCampaignLabel }}:
            {{ getCampaignSchedule }}
          </h4>
        </span>

        <button-nav
          class="float-right d-none d-sm-none d-md-flex"
          :buttons="avalableActions"
          @edit="editCampaign"
          @pauseCampaign="pauseCampaign"
          @resumeCampaign="resumeCampaign" />

      </p>
    </div>

    <table class="m-0 mt-4 w-100">
      <thead>
        <tr>
          <td>
            {{ $t('Campaign.preview.photo') }}
          </td>
          <td>
            {{ $t('Campaign.preview.title') }}
          </td>
          <td>
            {{ $t('Campaign.preview.price') }}
          </td>
          <td>
            {{ $t('Campaign.preview.discountedPrice') }}
          </td>
          <td>
            {{ $t('Campaign.preview.channel') }}
          </td>
          <td>
            {{ $t('Campaign.preview.time') }}
          </td>
          <td></td>
        </tr>
      </thead>

      <tbody
        v-for="e of getCampaignEvents"
        :key="e[0]">

        <tr>
          <td class="pt-3">
            <h5 class="m-0 day-label">
              {{ e[0] }}
            </h5>
          </td>
        </tr>

        <tr
          v-for="event of e[1]"
          :key="event.id">

          <td>
            <img
              width="50px"
              height="50px"
              :src="event.image"
              class="mr-3" />

          </td>
          <td>
            {{ event.label }}
          </td>

          <td>
            <span v-if="event.price.old">
              {{ event.price.old }} eur
            </span>
          </td>

          <td>
            <span v-if="event.price.new">
              {{ event.price.new }} eur
            </span>
          </td>

          <td>
            <q-image
              :pack="event.icon.pack"
              :name="event.icon.name"
              width="30px"
              height="30px"
              class="mr-2" />

            <span>
              {{ event.channel }}
            </span>
          </td>

          <td>
            {{ formatTime(event.time) }}
          </td>

          <td>
            <button
              class="action-buttons"
              :title="$t('Button.delete')"
              v-b-tooltip.hover
              @click="deleteMpe(event.id)">

              <icon
                name="trash"
                class="delete" />

            </button>

            <button
              class="action-buttons"
              :title="$t('Button.edit')"
              v-b-tooltip.hover
              @click="editMpe(event.id)">

              <icon
                name="edit"
                class="edit" />

            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="row m-0 m-0 bottom-buttons">
      <p class="col pl-0 pr-0 m-0">
        <button-nav
          class="float-right d-none d-sm-none d-md-flex"
          :buttons="avalableActions"
          @edit="editCampaign"
          @pauseCampaign="pauseCampaign"
          @resumeCampaign="resumeCampaign" />

      </p>
    </div>
  </div>
</template>

<script>
import InputButton from '@/components/standard/input/InputButton'
import ButtonNav from '@/components/standard/action/ButtonNav'
import QImage from '@/components/standard/display/QImage'
import RemoveWarning from '@/components/standard/notify/RemoveWarning'

import fieldBuilder from '@/assets/modules/apollo/fieldBuilder'

import moment from 'moment'

export default {
  data () {
    return {
      removalEvent: null
    }
  },

  methods: {
    /**
     * Formats post's time
     * @param {String} time
     * @returns {String}
     */
    formatTime(time) {
      return moment(time).format('hh.mm A')
    },

    /**
     * Deletes mpe from the campaign
     * @param {String} id
     */
    deleteMpe (id) {
      this.removalEvent = id
    },

    removeEvent (e) {
      this.$emit('deleteMpe', e)
      this.closeRemoveWarning()
    },

    closeRemoveWarning () {
      this.removalEvent = null
    },

    /**
     * Requests MPE editro
     * @param {String} id
     */
    editMpe (id) {
      this.$emit('editMpe', { id })
    },

    /**
     * Goes to campaign editor page
     */
    editCampaign () {
      this.$router.push({name: 'editCampaign', params: {id: this.getCampaignId, view: 'grid'}})
    },

    /**
     * Pauses campaign and goes to stopped campaign viewer
     */
    pauseCampaign () {
      this.toggleCampaign('paused', 'Kampanja je bila uspešno zaustavljena')
    },

    /**
     * Pauses campaign and goes to stopped campaign viewer
     */
    resumeCampaign () {
      this.toggleCampaign('active')
    },

    /**
     * Toggles campaign's status
     * @param {String} status Campaign's new status
     */
    toggleCampaign (status, customOkMessage = null) {
      let co = {
        id: this.getCampaignId,
        status,
        type: this.getCampaignType
      }

      let e = fieldBuilder
        .buildGqlObject(co, 'updateCampaign', 'mutation', [], {
          status: 'CampaignStatus',
          type: 'CampaignType'
        })

      e.okMessage = customOkMessage || 'Status kampanje posodobljen'
      e.failMessage = 'Status kampanje se ni posodobil'
      this.$emit('updateCampaign', e)
    }
  },

  computed: {
    /**
     * Gives event that requested removal
     * @returns {Object}
     */
    getRemoveEvent () {
      return {
        id: this.removalEvent
      }
    },

    /**
     * Tells if we are requesting to delete a event
     * @returns {Boolean}
     */
    hasRemoveEvent () {
      return this.removalEvent !== null
    },

    /**
     * Gives campaign info
     * @returns {Object}
     */
    getCampaignInfo () {
      return this.campaignData.campaignInfo || {}
    },

    /**
     * Gives campaign's id
     * @returns {String}
     */
    getCampaignId () {
      return this.getCampaignInfo.id
    },

    /**
     * Gives campaign's id
     * @returns {String}
     */
    getCampaignType () {
      return this.getCampaignInfo.type
    },

    /**
     * Gives campaign's label
     * @returns {String}
     */
    getCampaignLabel () {
      return this.getCampaignInfo.label
    },

    /**
     * Gives campaign's status
     * @returns {String}
     */
    getCampaignStatus () {
      return this.getCampaignInfo.status
    },

    /**
     * Gives campaign's schedule range
     * @returns {String}
     */
    getCampaignSchedule () {
      let { startsAt, expiresAt } = this.getCampaignInfo

      return `${moment(startsAt).format('D. M')} - ${moment(expiresAt).format('D. M. YYYY')}`
    },

    /**
     * Gives an array of events for this campaign - sorted by date
     * @returns {Array}
     */
    getCampaignEvents () {
      return Object.entries(this.campaignData.events || {})
        .sort( function (a, b) {
          console.log("__")
          let keyA = moment(a[0], 'DD. MM. YYYY'),
              keyB = moment(b[0], 'DD. MM. YYYY')

          return keyA.isSameOrAfter(keyB)
        })
    },

    /**
     * Gives available actions that this user can perform
     * @returns {Array}
     */
    avalableActions () {
      let rtr = []
      if (this.getCampaignStatus === 'active') {
        rtr.push({
          label: 'Button.stop',
          name: 'stop',
          event: 'pauseCampaign',
          style: 'nok-style',
          confirm: true
        })
      } else {
        rtr.push({
          label: 'Button.activate',
          name: 'start',
          event: 'resumeCampaign',
          style: 'activate-campaign-style',
          confirm: true
        })
      }

      return rtr
    },
  },

  components: {
    'input-button': InputButton,
    'button-nav': ButtonNav,
    'q-image': QImage,
    'remove-warning': RemoveWarning,
  },

  props: {
    campaignData: {
      type: Object,
      required: true,
      default: () => { return {} }
    }
  }
}
</script>

<style scoped>
.campaign-title, .day-label {
  font-size: 16px;
  line-height: 19px;
  font-weight: bold;
}
.day-label {
  font-size: 13px;
}

.event-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  margin-bottom: 8px;
}
thead{
  border-bottom: 1px solid #D3D3D3;
  font-weight: 600;
}
td{
  max-width: 200px;
  padding: 0 5px;
}
td img{
  margin-bottom: 20px;
}
tbody{
  border-bottom: 1px solid #D3D3D3;
  padding-bottom: 5px;
  font-weight: 300;
  font-size: 15px;
}
.action-buttons {
  padding: 5px;
  margin: 0;
  background-color: transparent;
  border: 0;
  outline: 0;
  cursor: pointer;
  transition: all .3s;
  align-items: center;
  justify-content: center;
  display: inline-flex;
}

.action-buttons .delete:hover {
  color: #FE2B41;
}

.action-buttons .edit:hover {
  color: #4C69BA;
}

.bottom-buttons {
  margin-top: 10px;
}

</style>



// WEBPACK FOOTER //
// src/components/service/campaignManager/campaignPreview/ActiveCampaignPreview.vue?8dd0fe32